/**
 * Scoring de l'égogramme (Analyse Transactionnelle).
 *
 * Deux couches strictement séparées :
 *  - `calculerScoresEgogramme` : fonction PURE sur un tableau de réponses
 *    normalisées. Aucun accès données, aucune dépendance Convex/React.
 *  - `adapterReponsesEgogramme` : adaptateur depuis `donnees.reponses` tel que
 *    la base le stocke (`formReponseEgogrammeValidator`, cf. convex/schema.ts).
 *
 * Le score est une SOMME BRUTE de 0/1. Aucune normalisation, aucun pourcentage,
 * aucune pondération, aucun seuil : `total` est fourni pour que la couche
 * présentation calcule un ratio si elle le souhaite.
 */

// ============================================
// CONSTANTES
// ============================================

/**
 * Libellés de réponse, centralisés : ils sont comparés en littéral exact
 * (accents compris) après `trim`, jamais réécrits en dur ailleurs.
 */
export const EGOGRAMME_REPONSE_VRAI = "Plutôt vrai";
export const EGOGRAMME_REPONSE_FAUX = "Plutôt faux";

/** Valeur numérique de chaque libellé. Toute autre valeur est invalide. */
const VALEURS_REPONSE: Record<string, number> = {
  [EGOGRAMME_REPONSE_VRAI]: 1,
  [EGOGRAMME_REPONSE_FAUX]: 0,
};

// ============================================
// TYPES
// ============================================

/** Entrée normalisée du calcul : une réponse candidat à une question. */
export interface ReponseEgogramme {
  /** Libellé de la question. */
  titre: string;
  /** Clé de profil (état du moi). Absente ou vide => réponse ignorée. */
  profil?: string;
  /** Libellé de réponse. Absent ou vide => question non répondue, ignorée. */
  reponse?: string;
}

export interface ScoreEgogramme {
  score: number;
  total: number;
}

export interface GroupeEgogramme extends ScoreEgogramme {
  /** Absent pour un profil sans underscore (cas `ADULTE`). */
  sousGroupes?: Record<string, ScoreEgogramme>;
}

export interface ResultatEgogramme {
  /** Groupes de premier niveau, indexés par nom de groupe (ex. "PARENT CRITIQUE"). */
  resultats: Record<string, GroupeEgogramme>;
  /** Liste plate des réponses RETENUES, dans l'ordre d'entrée. */
  reponses: { titre: string; reponse: string }[];
  /**
   * Nombre de réponses écartées parce que leur libellé n'est ni
   * `EGOGRAMME_REPONSE_VRAI` ni `EGOGRAMME_REPONSE_FAUX`.
   *
   * Choix explicite vis-à-vis de l'implémentation d'origine, qui renvoyait la
   * chaîne "ERROR" et l'additionnait au score (`0 + "ERROR"` → `"0ERROR"`,
   * score non numérique diffusé silencieusement). Ici la réponse est ignorée —
   * elle n'incrémente ni `score` ni `total` — et comptée ici. Un score reste
   * numérique par construction. Une clé de profil malformée, elle, reste une
   * erreur de programmation : elle lève (cf. `decouperProfil`).
   */
  invalides: number;
}

// ============================================
// REGROUPEMENT
// ============================================

interface ProfilDecoupe {
  groupe: string;
  sousGroupe?: string;
}

/**
 * Dérive groupe et sous-groupe DE LA CLÉ, sans table en dur :
 *  - `A_B_C` → groupe "A B", sous-groupe "C" ;
 *  - `A`     → groupe "A", pas de sous-groupe.
 *
 * Toute autre forme (1 underscore, ≥ 3 underscores, segment vide) lève. Dans
 * l'original, `PARENT_AIDANT` produisait un sous-groupe littéralement nommé
 * "undefined", et `A_B_C_D` perdait `D` en silence.
 */
export function decouperProfil(profil: string): ProfilDecoupe {
  const segments = profil.trim().split("_");

  if (segments.some((s) => s.length === 0)) {
    throw new Error(`Profil égogramme malformé (segment vide) : "${profil}"`);
  }

  if (segments.length === 1) return { groupe: segments[0]! };

  if (segments.length === 3) {
    return { groupe: `${segments[0]} ${segments[1]}`, sousGroupe: segments[2] };
  }

  throw new Error(
    `Profil égogramme malformé : "${profil}" — attendu 0 ou 2 underscores, ` +
      `reçu ${segments.length - 1}.`,
  );
}

/**
 * Ordre de lecture CANONIQUE des cinq états du moi, du Parent à l'Enfant.
 *
 * C'est l'ordre de l'égogramme en Analyse Transactionnelle, pas un classement :
 * il ne dépend NI des scores NI de l'alphabet. Le figer ici plutôt que dans
 * chaque vue garantit que l'histogramme et le tableau se lisent dans le même
 * sens — une barre et une carte au même rang désignent le même état.
 */
export const ORDRE_GROUPES_EGOGRAMME = [
  "PARENT CRITIQUE",
  "PARENT AIDANT",
  "ADULTE",
  "ENFANT LIBRE",
  "ENFANT ADAPTE",
] as const;

/**
 * Range les groupes calculés dans l'ordre canonique.
 *
 * Un groupe absent du résultat (aucune réponse sur ce profil) est simplement
 * omis — la vue ne doit pas afficher une barre à 0/0. Un groupe INCONNU du
 * référentiel (clé de profil ajoutée sans mise à jour de cette liste) est
 * conservé en fin de liste, par ordre alphabétique : le rendre invisible
 * masquerait une erreur de données.
 */
export function ordonnerGroupesEgogramme(
  resultats: Record<string, GroupeEgogramme>,
): [string, GroupeEgogramme][] {
  const rang = new Map<string, number>(
    ORDRE_GROUPES_EGOGRAMME.map((nom, i) => [nom, i]),
  );

  return Object.entries(resultats).sort(([a], [b]) => {
    const ra = rang.get(a);
    const rb = rang.get(b);
    if (ra !== undefined && rb !== undefined) return ra - rb;
    if (ra !== undefined) return -1;
    if (rb !== undefined) return 1;
    return a.localeCompare(b, "fr");
  });
}

// ============================================
// CALCUL
// ============================================

/**
 * Agrège les réponses en groupes / sous-groupes.
 *
 * Une réponse est ignorée si `profil` est absent/vide, ou si `reponse` est
 * absent/vide (question non répondue) : elle n'incrémente NI `score` NI
 * `total`. `total` est donc le nombre de réponses effectivement prises en
 * compte, pas le nombre de questions que le référentiel associe au profil : un
 * questionnaire partiel donne un `total` plus faible.
 */
export function calculerScoresEgogramme(
  reponses: ReponseEgogramme[],
): ResultatEgogramme {
  // Étape A — agrégation par profil ATOMIQUE (clé brute, non découpée).
  const parProfil = new Map<string, ScoreEgogramme>();
  const retenues: { titre: string; reponse: string }[] = [];
  let invalides = 0;

  for (const { titre, profil, reponse } of reponses) {
    const cle = profil?.trim();
    const libelle = reponse?.trim();
    if (!cle || !libelle) continue;

    const valeur = VALEURS_REPONSE[libelle];
    if (valeur === undefined) {
      invalides++;
      continue;
    }

    const courant = parProfil.get(cle) ?? { score: 0, total: 0 };
    courant.score += valeur;
    courant.total += 1;
    parProfil.set(cle, courant);

    retenues.push({ titre, reponse: libelle });
  }

  // Étape B — regroupement. Le total d'un groupe est, par construction, la
  // somme des totaux de ses sous-groupes.
  const resultats: Record<string, GroupeEgogramme> = {};

  for (const [cle, { score, total }] of parProfil) {
    const { groupe, sousGroupe } = decouperProfil(cle);
    const cible = (resultats[groupe] ??= { score: 0, total: 0 });

    if (sousGroupe !== undefined) {
      (cible.sousGroupes ??= {})[sousGroupe] = { score, total };
    }

    cible.score += score;
    cible.total += total;
  }

  return { resultats, reponses: retenues, invalides };
}

// ============================================
// ADAPTATEUR
// ============================================

/**
 * Forme stockée en base pour une réponse égogramme
 * (`formReponseEgogrammeValidator`, convex/schema.ts).
 */
export interface FormReponseEgogrammeStockee {
  name: string;
  label: string;
  profil: string;
  /** Position du Switch. `undefined` = question jamais répondue. */
  response?: boolean;
}

/**
 * Convertit `donnees.reponses` (record indexé par `name`) vers l'entrée du
 * calcul.
 *
 * `response === false` est un « non » PLEIN, pas une absence : il vaut
 * `EGOGRAMME_REPONSE_FAUX`, donc score +0 et total +1. Seul `undefined`
 * (question jamais touchée) produit une réponse sans libellé, donc ignorée.
 * Traiter `false` comme une non-réponse rendrait les totaux de groupe du
 * référentiel (ADULTE 44, ENFANT ADAPTE 30, ENFANT LIBRE 20, PARENT AIDANT 22,
 * PARENT CRITIQUE 24) inatteignables.
 */
export function adapterReponsesEgogramme(
  reponses:
    | Record<string, FormReponseEgogrammeStockee>
    | FormReponseEgogrammeStockee[]
    | undefined
    | null,
): ReponseEgogramme[] {
  if (!reponses) return [];
  const liste = Array.isArray(reponses) ? reponses : Object.values(reponses);

  return liste.map(({ label, profil, response }) => ({
    titre: label,
    profil,
    reponse:
      response === undefined
        ? undefined
        : response
          ? EGOGRAMME_REPONSE_VRAI
          : EGOGRAMME_REPONSE_FAUX,
  }));
}
